import { Box, Checkbox, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../form";
import type { VisualFormat } from "../../types/dashboard";

const DEFAULT_SERIES_COLORS = [
  "#5470c6",
  "#91cc75",
  "#fac858",
  "#ee6666",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
];

type Props = {
  value?: VisualFormat;
  onChange: (format: VisualFormat) => void;
  showMobileToggle?: boolean;
  mobileStacked?: boolean;
  onToggleMobile?: () => void;
};

function FormCheck({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Checkbox size="small" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

function normalizeHex(value: string | undefined, fallback = "#ffffff"): string {
  const raw = (value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const [, a, b, c] = raw;
    return `#${a}${a}${b}${b}${c}${c}`;
  }
  return fallback;
}

export default function VisualFormatControls({
  value,
  onChange,
  showMobileToggle,
  mobileStacked,
  onToggleMobile,
}: Props) {
  const { t } = useTranslation();
  const format = value || {};
  const colors = format.colors || [];

  const patch = (partial: VisualFormat) => onChange({ ...format, ...partial });

  const setColorAt = (index: number, color: string) => {
    const next = [...colors];
    next[index] = color;
    patch({ colors: next });
  };

  const removeColorAt = (index: number) => {
    patch({ colors: colors.filter((_, i) => i !== index) });
  };

  const addColor = () => {
    const nextColor = DEFAULT_SERIES_COLORS[colors.length % DEFAULT_SERIES_COLORS.length];
    patch({ colors: [...colors, nextColor] });
  };

  const applyPreset = () => {
    patch({ colors: [...DEFAULT_SERIES_COLORS] });
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {t("reportDesigner.formatHint")}
      </Typography>

      <FormCheck
        label={t("reportDesigner.legend")}
        checked={format.showLegend !== false}
        onChange={(checked) => patch({ showLegend: checked })}
      />
      <FormCheck
        label={t("reportDesigner.labels")}
        checked={!!format.showDataLabels}
        onChange={(checked) => patch({ showDataLabels: checked })}
      />
      <FormCheck
        label={t("reportDesigner.stacked")}
        checked={!!format.stacked}
        onChange={(checked) => patch({ stacked: checked })}
      />
      <FormCheck
        label={t("reportDesigner.average")}
        checked={!!format.showAverageLine}
        onChange={(checked) => patch({ showAverageLine: checked })}
      />

      <Box>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
          {t("reportDesigner.background")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, lineHeight: 1.35 }}>
          {t("reportDesigner.backgroundHint")}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component="input"
            type="color"
            value={normalizeHex(format.backgroundColor, "#ffffff")}
            onChange={(event) => patch({ backgroundColor: event.target.value })}
            aria-label={t("reportDesigner.background")}
            sx={{
              width: 40,
              height: 36,
              p: 0.25,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
          <AppTextField
            size="small"
            fullWidth
            label={t("reportDesigner.backgroundHex")}
            placeholder="#ffffff"
            value={format.backgroundColor || ""}
            onChange={(event) => patch({ backgroundColor: event.target.value })}
          />
          <AppButton
            size="small"
            variant="outlined"
            onClick={() => patch({ backgroundColor: undefined })}
            disabled={!format.backgroundColor}
          >
            {t("reportDesigner.clear")}
          </AppButton>
        </Stack>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {t("reportDesigner.colors")}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <AppButton size="small" variant="text" onClick={applyPreset}>
              {t("reportDesigner.colorsPreset")}
            </AppButton>
            <Tooltip title={t("reportDesigner.colorsAdd")}>
              <IconButton size="small" onClick={addColor} aria-label={t("reportDesigner.colorsAdd")}>
                <AddOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.35 }}>
          {t("reportDesigner.colorsHint")}
        </Typography>
        {colors.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {t("reportDesigner.colorsEmpty")}
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {colors.map((color, index) => (
              <Stack key={`color-${index}`} direction="row" spacing={1} alignItems="center">
                <Box
                  component="input"
                  type="color"
                  value={normalizeHex(color, DEFAULT_SERIES_COLORS[0])}
                  onChange={(event) => setColorAt(index, event.target.value)}
                  aria-label={`${t("reportDesigner.colors")} ${index + 1}`}
                  sx={{
                    width: 40,
                    height: 36,
                    p: 0.25,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                />
                <AppTextField
                  size="small"
                  fullWidth
                  value={color}
                  onChange={(event) => setColorAt(index, event.target.value)}
                />
                <IconButton
                  size="small"
                  onClick={() => removeColorAt(index)}
                  aria-label={t("reportDesigner.colorsRemove")}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {showMobileToggle && onToggleMobile && (
        <AppButton
          size="small"
          variant={mobileStacked ? "contained" : "outlined"}
          color="secondary"
          onClick={onToggleMobile}
        >
          {t("reportDesigner.mobile")}
        </AppButton>
      )}
    </Stack>
  );
}
